import { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Settings, 
  LogOut, 
  Shield,
  MapPin,
  Loader2,
  Bug,
  Brain,
  MessageSquare,
  Star,
  Inbox,
  GraduationCap,
  Handshake,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ConnectionIndicator } from '@/components/ConnectionIndicator';
import logoImg from '@/assets/logo-standfull.png';
import { APP_VERSION, BUILD_ID, compareVersions } from '@/lib/buildInfo';

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', shortLabel: 'Dash', end: true },
  { to: '/admin/checkins', icon: MapPin, label: 'Check-ins', shortLabel: 'Check' },
  { to: '/admin/operadores', icon: Users, label: 'Operadores', shortLabel: 'Ops' },
  { to: '/admin/treinos', icon: GraduationCap, label: 'Treinos', shortLabel: 'Treino' },
  { to: '/admin/avaliacoes', icon: Star, label: 'Avaliações', shortLabel: 'Aval' },
  { to: '/admin/ia', icon: Brain, label: 'IA', shortLabel: 'IA' },
  { to: '/admin/whatsapp', icon: MessageSquare, label: 'WhatsApp', shortLabel: 'Whats' },
  { to: '/admin/influencers', icon: Handshake, label: 'Influencers', shortLabel: 'Influ' },
  { to: '/admin/fale-conosco', icon: Inbox, label: 'Fale Conosco', shortLabel: 'Msgs' },
  { to: '/admin/config', icon: Settings, label: 'Config', shortLabel: 'Config' },
  { to: '/admin/diagnostics', icon: Bug, label: 'Diag', shortLabel: 'Diag' },
];

export const AdminLayout = () => {
  const { signOut, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loggingOut, setLoggingOut] = useState(false);
  const hasResetLegacyCacheRef = useRef(false);

  useEffect(() => {
    if (hasResetLegacyCacheRef.current) return;
    hasResetLegacyCacheRef.current = true;

    if (!location.pathname.startsWith('/admin')) return;

    const swResetKey = `standfull-admin-sw-reset-${APP_VERSION}`;
    if (sessionStorage.getItem(swResetKey)) return;

    const hasServiceWorkerController = typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller;
    if (!hasServiceWorkerController) return;

    const resetLegacyCacheAndReload = async () => {
      try {
        if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map((registration) => registration.unregister()));
        }

        if (typeof window !== 'undefined' && 'caches' in window) {
          const cacheKeys = await caches.keys();
          await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
        }

        sessionStorage.setItem(swResetKey, '1');
        const url = new URL(window.location.href);
        url.searchParams.set('_cache_reset', APP_VERSION);
        window.location.replace(url.toString());
      } catch (error) {
        console.warn('[AdminLayout] Falha ao resetar cache legado:', error);
      }
    };

    resetLegacyCacheAndReload();
  }, [location.pathname]);

  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) return;

    const ensureFreshAdminShell = async () => {
      try {
        const response = await fetch(`/version.json?_cb=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
          },
        });

        if (!response.ok) return;

        const serverVersion = await response.json() as { buildId?: string; version?: string };
        const serverBuildId = serverVersion.buildId;
        const serverSemver = serverVersion.version ?? APP_VERSION;
        const shouldRefresh = Boolean(serverBuildId) && (
          serverBuildId !== BUILD_ID || compareVersions(serverSemver, APP_VERSION) > 0
        );

        if (!shouldRefresh) return;

        const refreshLockKey = `standfull-admin-refresh-${serverBuildId}`;
        if (sessionStorage.getItem(refreshLockKey)) return;

        sessionStorage.setItem(refreshLockKey, '1');
        const url = new URL(window.location.href);
        url.searchParams.set('_v', serverBuildId as string);
        window.location.replace(url.toString());
      } catch (error) {
        console.warn('[AdminLayout] Falha ao validar versão no login admin:', error);
      }
    };

    ensureFreshAdminShell();
  }, [location.pathname]);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    
    try {
      const { error } = await signOut();
      
      if (error) {
        console.error('[AdminLayout] Logout error:', error);
        toast.error('Erro ao sair. Tente novamente.');
        setLoggingOut(false);
        return;
      }
      
      toast.success('Logout realizado');
      
      // Force navigate to login as fallback
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('[AdminLayout] Logout exception:', err);
      toast.error('Erro ao sair');
      // Force navigate even on error
      navigate('/login', { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col bg-background">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <img src={logoImg} alt="StandFull" loading="lazy" decoding="async" className="h-8 w-auto rounded" />
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <span className="font-semibold text-foreground">Admin</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3">
          <NavLink
            to="/admin/influencers"
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                isActive
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`
            }
          >
            <Handshake className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Influencers</span>
          </NavLink>

          <span className="text-sm text-muted-foreground hidden sm:block">
            {profile?.nome}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            disabled={loggingOut}
            className="text-muted-foreground hover:text-foreground"
          >
            {loggingOut ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogOut className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      <ConnectionIndicator />

      {/* Navigation - grid no mobile para nunca ocultar abas */}
      <nav className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-11 gap-1 px-3 sm:px-4 py-2 border-b border-border bg-card/30">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `w-full min-w-0 flex items-center justify-center gap-1 px-1.5 py-1.5 min-h-[36px] md:min-h-[40px] rounded-lg text-[10px] md:text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`
            }
          >
            <item.icon className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
            <span className="truncate md:hidden">{item.shortLabel}</span>
            <span className="truncate hidden md:inline">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Content - with standard container */}
      <main className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 md:py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
